import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Card,
  DataTable,
  EmptyState,
  type BadgeProps,
  type DataTableColumn,
} from "@pericles/framework-frontend";

import { getCoverageMatrix, validateCoverageMatrix } from "../api/projectCoverageMatrixClient";
import type {
  CoverageMatrix,
  CoverageMatrixRow,
  CoverageMatrixValidationResponse,
  CoverageStatus,
} from "../types/coverageMatrix";

import { InlineAlert } from "./InlineAlert";

interface CoverageMatrixPanelProps {
  projectId: string;
}

const STATUS_BADGES: Record<string, { label: string; variant: BadgeProps["variant"] }> = {
  covered: { label: "Covered", variant: "success" },
  partially_covered: { label: "Partially covered", variant: "warning" },
  missing: { label: "Missing", variant: "danger" },
};

const STATUS_ORDER: Record<string, number> = {
  missing: 0,
  partially_covered: 1,
  covered: 2,
};

function renderStatusBadge(status: CoverageStatus): JSX.Element {
  const config = STATUS_BADGES[status] ?? { label: status, variant: "default" };
  return <Badge label={config.label} variant={config.variant} />;
}

function formatDate(value?: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function renderIdList(items: string[]): JSX.Element {
  if (!items?.length) {
    return <span className="text-xs text-slate-600">None</span>;
  }

  return (
    <ul className="flex list-disc flex-col gap-1 pl-4 text-xs text-slate-800">
      {items.map((item) => (
        <li key={item} className="break-words">
          {item}
        </li>
      ))}
    </ul>
  );
}

function renderReasons(reasons: string[]): JSX.Element {
  if (!reasons?.length) {
    return <span className="text-xs text-slate-600">None</span>;
  }

  return (
    <ul className="flex list-disc flex-col gap-1 pl-4 text-xs text-slate-800">
      {reasons.map((reason, index) => (
        <li key={`${reason}-${index}`} className="break-words">
          {reason}
        </li>
      ))}
    </ul>
  );
}

export function CoverageMatrixPanel({ projectId }: CoverageMatrixPanelProps): JSX.Element {
  const [matrix, setMatrix] = useState<CoverageMatrix | null>(null);
  const [matrixPath, setMatrixPath] = useState<string | null>(null);
  const [lastModifiedAt, setLastModifiedAt] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<CoverageStatus | "all">("all");
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationMessage, setValidationMessage] = useState<CoverageMatrixValidationResponse | null>(null);

  const artifactsById = useMemo(() => {
    const map = new Map<string, { path?: string; description?: string }>();
    (matrix?.generatedArtifacts ?? []).forEach((artifact) => {
      if (artifact?.id) {
        map.set(artifact.id, { path: artifact.path, description: artifact.description });
      }
    });
    return map;
  }, [matrix]);

  const coverageRows = useMemo(() => {
    const rows = matrix?.coverage ?? [];
    const filtered = statusFilter === "all" ? rows : rows.filter((row) => row.status === statusFilter);
    return [...filtered].sort((a, b) => {
      const statusRankA = STATUS_ORDER[a.status] ?? Number.MAX_SAFE_INTEGER;
      const statusRankB = STATUS_ORDER[b.status] ?? Number.MAX_SAFE_INTEGER;
      if (statusRankA !== statusRankB) {
        return statusRankA - statusRankB;
      }
      return (a.requirementId ?? "").localeCompare(b.requirementId ?? "");
    });
  }, [matrix, statusFilter]);

  const columns = useMemo<DataTableColumn<CoverageMatrixRow>[]>(
    () => [
      {
        id: "briefItemId",
        header: "Brief item",
        accessor: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-semibold text-slate-900">{row.briefItemId}</span>
            <span className="text-xs text-slate-600">{row.requirementId}</span>
          </div>
        ),
        width: 240,
      },
      {
        id: "dslNodes",
        header: "DSL nodes",
        accessor: (row) => renderIdList(row.dslNodeIds),
      },
      {
        id: "artifacts",
        header: "Generated artifacts",
        accessor: (row) => {
          if (!row.generatedArtifactIds?.length) {
            return <span className="text-xs text-slate-600">None</span>;
          }

          return (
            <ul className="flex list-disc flex-col gap-1 pl-4 text-xs text-slate-800">
              {row.generatedArtifactIds.map((artifactId) => {
                const artifact = artifactsById.get(artifactId);
                return (
                  <li key={artifactId} className="break-words">
                    <div className="font-semibold">{artifactId}</div>
                    {artifact?.path ? (
                      <div className="text-[11px] text-slate-600">{artifact.path}</div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        accessor: (row) => renderStatusBadge(row.status),
        width: 140,
        align: "center",
      },
      {
        id: "reasons",
        header: "Reasons",
        accessor: (row) => renderReasons(row.reasons),
      },
    ],
    [artifactsById],
  );

  const loadMatrix = async () => {
    if (!projectId) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await getCoverageMatrix(projectId);
      setMatrix(result.matrix);
      setMatrixPath(result.path);
      setLastModifiedAt(result.lastModifiedAt);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load coverage matrix";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadMatrix();
  }, [projectId]);

  const handleValidate = async () => {
    if (!projectId) return;
    setIsValidating(true);
    setValidationMessage(null);

    try {
      const result = await validateCoverageMatrix(projectId);
      setValidationMessage(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to validate coverage matrix";
      setValidationMessage({ ok: false, error: message });
    } finally {
      setIsValidating(false);
    }
  };

  const lastUpdated = formatDate(lastModifiedAt ?? undefined);

  return (
    <Card title="Coverage matrix">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <div className="text-sm font-semibold text-slate-800">
              {matrixPath ? `Path: ${matrixPath}` : "Coverage matrix path unavailable"}
            </div>
            {lastUpdated ? (
              <div className="text-xs text-slate-600">Last modified {lastUpdated}</div>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="button button-secondary"
              onClick={loadMatrix}
              disabled={isLoading}
            >
              {isLoading ? "Refreshing..." : "Refresh"}
            </button>
            <button
              type="button"
              className="button button-primary"
              onClick={handleValidate}
              disabled={isValidating}
            >
              {isValidating ? "Validating..." : "Validate"}
            </button>
          </div>
        </div>

        {error ? <InlineAlert tone="danger" title={error} /> : null}

        {validationMessage ? (
          <div className="flex flex-col gap-2">
            <InlineAlert
              tone={validationMessage.ok ? "success" : "danger"}
              title={validationMessage.ok ? "Coverage matrix is valid" : "Validation failed"}
              description={
                validationMessage.ok
                  ? undefined
                  : validationMessage.error ?? validationMessage.errors?.[0] ?? "Unknown validation error"
              }
            />
            {!validationMessage.ok && validationMessage.errors?.length ? (
              <ul className="ml-1 list-disc pl-4 text-sm text-slate-800">
                {validationMessage.errors.map((entry, index) => (
                  <li key={`${entry}-${index}`} className="break-words">
                    {entry}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-semibold text-slate-800">Entries</div>
          <div className="flex items-center gap-2">
            <label htmlFor="coverage-status-filter" className="text-xs font-semibold text-slate-700">
              Status
            </label>
            <select
              id="coverage-status-filter"
              className="rounded border border-slate-200 bg-white px-2 py-1 text-sm"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as CoverageStatus | "all")}
            >
              <option value="all">All</option>
              <option value="covered">Covered</option>
              <option value="partially_covered">Partially covered</option>
              <option value="missing">Missing</option>
            </select>
          </div>
        </div>

        <DataTable<CoverageMatrixRow>
          columns={columns}
          data={coverageRows}
          isLoading={isLoading}
          emptyState={
            <EmptyState
              title="No coverage matrix rows"
              description="Refresh to load coverage entries from the backend."
            />
          }
        />
      </div>
    </Card>
  );
}
